import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params } from '@angular/router';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { BookRequest, BookResponse, TagResponse } from '@builtbybros/al-model';
import { Observable, Subject } from 'rxjs';

import { BookService } from '../book.service';
import { AlertService } from '../../../shared/alert.service';
import { HttpService } from '../../../shared/http.service';
import { FormsService } from '../../../shared/forms.service';
import { QueryParam } from '../../../shared/query-param';
import { ImageUploaderComponent } from '../../../image-uploader/image-uploader/image-uploader.component';

@Component({
  selector: 'app-book-form',
  templateUrl: './book-form.component.html',
  styleUrls: [
    './book-form.component.scss'
  ],
  encapsulation: ViewEncapsulation.None,
})
export class BookFormComponent implements OnInit {
  url = '/Book/';
  tagUrl = '/Tag/';
  postUrl = '/upload';
  previousCoverUrl: string | undefined = undefined;
  bookId: number;
  bookForm: FormGroup;
  btnMsg: string;
  h1: string;
  file: File;
  fileChanged = false;
  fileDeleted = false;
  stock = '';
  editorOptions = {
    modules: {
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['clean'],
        ['video'],
      ]
    },
  };
  msg = {
    'deleteImageSuccess': 'Imagen eliminada con éxito',
    'deleteImageError': 'Ocurrió un error al eliminar la imagen',
    'submitError': 'Ocurrió un error al procesar el formulario',
    'updateBookSuccess': 'Libro actualizado correctamente'
  };

  @ViewChild(ImageUploaderComponent)
  private imageUploader: ImageUploaderComponent;

  itemIds: number[] = [];
  tagsSelected: TagResponse.Tag[] = [];

  // Search
  searchResultItems: Observable<TagResponse.Tag[]>;
  private searchTerms = new Subject<string>();
  private searching = false;

  constructor(private route: ActivatedRoute,
              private fb: FormBuilder,
              private formsService: FormsService,
              private alertService: AlertService,
              private bookService: BookService,
              private httpService: HttpService,
              private location: Location) {
    this.createFormGroup();
  }

  ngOnInit() {
    this.route.params.subscribe((params: Params) => {
      if (params['bookId']) {
        // Update tag
        // Get the tag with id = PromoId
        this.h1 = 'Editar Libro';
        this.btnMsg = 'Actualizar';
        this.bookId = +params['bookId'];
        const responseExpected = new BookResponse.Book();
        this.httpService.get(this.url, this.bookId, responseExpected)
          .subscribe(
            bookResponse => {
              this.bookForm.patchValue(bookResponse);
              for (let tag of bookResponse.tags) {
                this.tagsSelected.push(tag);
                this.itemIds.push(tag.id);
              }
              if (bookResponse.imgCoverUrl) {
                this.previousCoverUrl = this.httpService.getCoverUrl(bookResponse.imgCoverUrl);
                this.httpService.downloadImage(`${bookResponse.isbn}.jpg`).subscribe(
                  imgResponse => {
                    const acceptedFiles: File[] = [];
                    acceptedFiles.push(imgResponse);
                    this.imageUploader.fileChange(<any> acceptedFiles);
                  }
                );
              }
              this.httpService.getRaw(`/Book/Stock/${bookResponse.isbn}`, []).subscribe(
                stockResponse => {
                  this.stock = (stockResponse) ? `${stockResponse}` : 'Dato no disponible';
                }
              );
            },
            error => {
              this.alertService.error(error);
            }
          );
      }
    });

    this.searchResultItems = this.searchTerms
      .do(() => this.searching = true)
      .switchMap(term => term ? this.searchItem(term) : Observable.of<TagResponse.Tag[]>([]))
      .catch(error => {
        this.alertService.error(error);
        return Observable.of<TagResponse.Tag[]>([]);
      })
      .do(() => this.searching = false);
  }

  search(term: string): void {
    this.searchTerms.next(term);
  }

  private searchItem(query: string): Observable<TagResponse.Tag[]> {
    const params = new Array<QueryParam>();
    params[0] = new QueryParam('pagination', '10');
    params[1] = new QueryParam('query', query);

    const searchUrl = '/TagSearch';
    const resultObservable: Observable<TagResponse.All> = this.httpService.getWithParams(
      searchUrl, params, TagResponse.All);


    return resultObservable
      .map(itemResponse => {
          return itemResponse.objects;
        },
        error => {
          console.log('error', error);
          return [];
        }
      );
  }

  assignItems() {
    return this.itemIds.map(String).join();
  }

  submitForm() {
    if (this.formsService.validateFormAndUpdateErrors(this.bookForm)) {
      const isbn = this.bookForm.get('isbn').value;
      if (this.fileDeleted) {
        this.httpService.delete(`${this.postUrl}/${isbn}`)
          .subscribe(
            updateResponse => {
              this.updateBook();
              this.alertService.success(this.msg['deleteImageSuccess']);
            },
            error => {
              this.alertService.error(this.msg['deleteImageError']);
            }
          );
      } else if (this.fileChanged) {
        this.httpService.postFile(this.postUrl, this.file, this.bookForm.get('isbn').value)
          .subscribe(
            uploadResponse => {
              this.updateBook();
            },
            error => {
              alert('Error en la carga de imagen');
              this.alertService.error('Error en la carga de imagen');
            }
          );
      } else {
        this.updateBook();
      }
    } else {
      this.alertService.error(this.msg['submitError']);
    }
    return;
  }

  cleanSearch() {
    this.searchTerms.next('');
  }

  addItem(value: number) {
    if (this.itemIds.indexOf(value) === -1) {
      let responseExpected = new TagResponse.Tag();
      this.httpService.get(this.tagUrl, value, responseExpected)
        .subscribe(
          createResponse => {
            if (createResponse) {
              this.tagsSelected.push(createResponse);
              this.itemIds.push(createResponse.id);
            }
          }
        );
    }
  }

  delete(value: number) {
    let index = this.itemIds.indexOf(+value);
    if (index !== -1) {
      this.itemIds.splice(index, 1);
      this.tagsSelected.splice(index, 1);
    }
  }

  deleteImage(event: any) {
    this.fileDeleted = true;
    this.fileChanged = false;
    this.file = null;
  }

  imageUpload(file: File) {
    this.fileChanged = true;
    this.file = file;
    this.previousCoverUrl = file.name;
    this.fileDeleted = false;
  }

  back() {
    this.location.back();
  }

  private updateBook() {
    if (this.bookId) {
      // Update tag
      let book = new BookRequest.Update();
      book.parse(this.bookForm.value);
      if (this.bookForm.get('imageUrl').value) {
        book.imgCoverUrl = this.bookForm.get('imageUrl').value.replace(/.jpg/gi, '');
      } else if (this.file) {
        book.imgCoverUrl = this.bookForm.get('isbn').value + '.jpg';
      } else if (!this.file && this.previousCoverUrl) {
        book.imgCoverUrl = this.previousCoverUrl.replace('https://imagenesal.bros.me/', '');
      }
      book.tags = this.assignItems();
      this.bookService.updateBook(this.bookId, book)
        .subscribe(
          createResponse => {
            this.alertService.success(this.msg['updateBookSuccess']);
          },
          error => {
            this.alertService.error(error);
          }
        );
    }
  }

  private createFormGroup() {
    this.bookForm = this.fb.group({
      'isbn': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(15)
        ])
      ],
      'title': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(100)
        ])
      ],
      'price': [
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^\d+$/),
          Validators.maxLength(10)
        ])
      ],
      'editorial': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(100)
        ])
      ],
      'author': [
        null,
        Validators.compose([
          Validators.maxLength(100)
        ])
      ],
      'review': [
        null,
        Validators.compose([
          Validators.maxLength(2500)
        ])
      ],
      'format': [
        null,
        Validators.compose([
          Validators.maxLength(50)
        ])
      ],
      'weight': [
        null,
        Validators.compose([
          Validators.pattern(/^[0-9]{1,3}\.?[0-9]{0,3}$/), // accept values as integers or decimals: 0, 0.3, etc
          Validators.maxLength(50)
        ])
      ],
      'imageUrl': [
        null,
        Validators.maxLength(2500)
      ],
      'isActive': [
        true,
        null
      ]
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/book/shared/book-form/book-form.component.ts